<template>
  <div class="gate-page">
    <!-- 右上角 logo -->
    <div class="logo">
      <img src="@/assets/images/logo.png" alt="Logo" />
    </div>
    <!-- 主体：单一卡片容器 -->
    <div class="page-body">
      <div class="platform-card" :class="{ 'brand-col-main': currentRole }">
        <!-- 左侧品牌列 -->
        <div
          class="brand-col"
          :class="{ 'brand-col-no-current': !currentRole }"
        >
          <div class="main-title">
            临航<span class="dot">·</span>中心智选平台
          </div>
          <div class="subhead">
            临床试验一体化智能协作平台，共创多方共赢新生态
          </div>

          <!-- 动态滚动状态列表 -->
          <div class="landing-status">
            <div class="landing-status-title">
              <img
                class="landing-status-icon"
                src="@/assets/images/icon-sponsor.png"
              />
              合作落地项目动态
            </div>
            <div
              class="landing-status-window"
              @mouseenter="pauseScroll"
              @mouseleave="resumeScroll"
            >
              <ul
                class="landing-status-track"
                :class="{ paused: scrollPaused }"
              >
                <li
                  v-for="(item, idx) in scrollItems"
                  :key="idx"
                  class="landing-status-item"
                  :title="item"
                >
                  <div class="landing-status-text">{{ item }}</div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- 右侧交互列 -->
        <div class="interact-col">
          <!-- 角色卡片 -->
          <div class="role-grid">
            <div
              v-for="role in roles"
              :key="role.key"
              class="role-card"
              :class="{ active: currentRole === role.key }"
              @click="selectRole(role.key)"
            >
              <div class="role-icon">
                <img :src="role.icon" :alt="role.name" />
              </div>
              <div class="role-name">{{ role.name }}</div>
              <div class="role-desc" v-html="role.desc"></div>
            </div>
          </div>

          <!-- 登录表单 -->
          <transition name="slide-down">
            <div v-if="currentRole" class="login-form-wrap">
              <a-form :form="loginForm" @submit.prevent="handleLogin">
                <a-form-item>
                  <a-input
                    v-decorator="[
                      'username',
                      { rules: [{ required: true, message: '请输入账号' }] },
                    ]"
                    :placeholder="accountPlaceholder"
                    size="large"
                    allow-clear
                  >
                    <a-icon slot="prefix" type="user" style="color: #c0ccd8" />
                  </a-input>
                </a-form-item>

                <a-form-item>
                  <a-input-password
                    v-decorator="[
                      'password',
                      { rules: [{ required: true, message: '请输入密码' }] },
                    ]"
                    placeholder="请输入密码"
                    size="large"
                  >
                    <a-icon slot="prefix" type="lock" style="color: #c0ccd8" />
                  </a-input-password>
                </a-form-item>

                <a-form-item v-if="currentRole === 'smo'">
                  <a-select
                    v-decorator="[
                      'smoMenuType',
                      { initialValue: '2', rules: [{ required: true }] },
                    ]"
                    size="large"
                    placeholder="请选择登录角色"
                  >
                    <a-select-option value="2">报名方</a-select-option>
                    <a-select-option value="1">发布方</a-select-option>
                  </a-select>
                </a-form-item>

                <a-form-item v-if="currentRole !== 'institution'">
                  <div class="captcha-row">
                    <a-input
                      v-decorator="[
                        'code',
                        {
                          rules: [{ required: true, message: '请输入验证码' }],
                        },
                      ]"
                      placeholder="请输入验证码"
                      size="large"
                      style="flex: 1"
                    />
                    <div class="captcha-box" @click="refreshCaptcha">
                      <img
                        v-if="captchaEnabled && codeUrl"
                        :src="codeUrl"
                        alt="验证码"
                      />
                      <span v-else>点击刷新</span>
                    </div>
                  </div>
                </a-form-item>

                <div class="form-actions">
                  <a-checkbox v-model="rememberMe">记住密码</a-checkbox>
                  <a class="forgot-link" @click="openForgotModal">忘记密码？</a>
                </div>

                <a-button
                  type="primary"
                  html-type="submit"
                  size="large"
                  block
                  :loading="loginLoading"
                  class="btn-login"
                >
                  立即登录
                </a-button>
              </a-form>
            </div>
          </transition>

          <div class="card-footer">
            <div
              v-if="currentRole == 'sponsor'"
              class="no-login-footer cursor-pointer"
              @click="openWx"
            >
              申办方/CRO中心筛选调研的项目，建议通过微信公众号：驭时GCP信息，底部菜单快速发项目，移
              动端体验更优。
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 忘记密码弹窗 -->
    <a-modal
      v-model="forgotVisible"
      title="忘记密码"
      :footer="null"
      width="520px"
      class="forgot-modal"
    >
      <forgot-password-form
        :role="currentRole"
        @close="forgotVisible = false"
      />
    </a-modal>

    <!-- 临床机构选择弹窗 -->
    <a-modal
      v-model="institutionModalVisible"
      title="选择登录机构"
      :confirm-loading="loginLoading"
      ok-text="确认登录"
      cancel-text="取消"
      width="400px"
      @ok="confirmInstitutionSite"
      @cancel="institutionModalVisible = false"
    >
      <a-spin :spinning="institutionSiteLoading">
        <a-select
          v-model="selectedSiteId"
          style="width: 100%"
          size="large"
          placeholder="请选择登录机构"
          :not-found-content="
            institutionSiteLoading ? undefined : '未找到可登录的机构'
          "
        >
          <a-select-option
            v-for="site in institutionSites"
            :key="site.site_id"
            :value="site.site_id"
          >
            {{ site.site_name }}
          </a-select-option>
        </a-select>
      </a-spin>
    </a-modal>
  </div>
</template>

<script>
import Cookies from "js-cookie";
import api from "@/plugins/api";
import surveyApi from "@/plugins/api/common/survey";
import ForgotPasswordForm from "./components/ForgotPasswordForm.vue";
import iconSponsor from "@/assets/images/icon-sponsor.png";
import iconSmo from "@/assets/images/icon-smo.png";
import iconInstitution from "@/assets/images/icon-institution.png";
import agent from "@/assets/images/icon-agent.png";

export default {
  name: "LoginPage",
  components: { ForgotPasswordForm },
  data() {
    return {
      loginForm: this.$form.createForm(this),
      currentRole: null,
      loginLoading: false,
      rememberMe: false,
      captchaEnabled: false,
      codeUrl: "",
      captchaUuid: "",
      forgotVisible: false,
      institutionModalVisible: false,
      institutionSiteLoading: false,
      institutionSites: [],
      selectedSiteId: null,
      pendingInstitutionCredentials: null,
      scrollPaused: false,
      scrollItems: [],
      roles: [
        {
          key: "sponsor",
          name: "申办方 / CRO",
          desc: "发布中心筛选需求<br />发起SMO招标、项目管理与追踪",
          icon: iconSponsor,
        },
        {
          key: "smo",
          name: "SMO",
          desc: "承接SMO项目、机构推荐<br />分包协作、执行反馈",
          icon: iconSmo,
        },
        {
          key: "agent",
          name: "项目运营商",
          desc: "机构项目运营管理<br />报名进度跟进、促进项目落地",
          icon: agent,
        },
        {
          key: "institution",
          name: "临床试验机构",
          desc: "承接试验项目<br />查看中心筛选需求、洽谈合作",
          icon: iconInstitution,
        },
      ],
    };
  },
  computed: {
    accountPlaceholder() {
      return this.currentRole === "institution"
        ? "请输入账号/手机号"
        : "请输入邮箱/手机号";
    },
  },
  mounted() {
    this.loadSavedCredentials();
    this.loadScrollItems();
  },
  methods: {
    openWx() {
      window.location.href = "http://site.yscro.com/";
    },
    async loadScrollItems() {
      try {
        const res = await surveyApi.getCollaborationLaunchList();
        const items = (res.data || []).map((item) => item.content);
        this.scrollItems = [...items, ...items];
      } catch {
        // 接口失败时保持空列表，不影响登录功能
      }
    },
    selectRole(roleKey) {
      const prevRole = this.currentRole;
      this.currentRole = roleKey;
      localStorage.setItem("gate_last_role", roleKey);
      this.$nextTick(() => {
        // 切换平台时清空账号密码（防止浏览器自动填充串平台）
        if (prevRole !== roleKey) {
          this.loginForm.setFieldsValue({
            username: "",
            password: "",
            code: "",
          });
        }
        if (roleKey !== "institution") {
          this.refreshCaptcha();
        }
      });
    },
    pauseScroll() {
      this.scrollPaused = true;
    },
    resumeScroll() {
      this.scrollPaused = false;
    },
    async refreshCaptcha() {
      try {
        const mod =
          this.currentRole === "smo"
            ? api().smo.login
            : this.currentRole === "agent"
            ? api().agent.login
            : api().sponsor.login;
        const res = await mod.getCodeImg();
        if (this.currentRole === "agent") {
          this.captchaEnabled = true;
          this.codeUrl = res.data.captcha_image;
          this.captchaUuid = res.data.captcha_key;
        } else {
          this.captchaEnabled = res.captchaEnabled !== false;
          if (this.captchaEnabled) {
            this.codeUrl = "data:image/gif;base64," + res.img;
            this.captchaUuid = res.uuid;
          }
        }
      } catch {
        this.captchaEnabled = false;
      }
    },
    handleLogin() {
      this.loginForm.validateFields(async (err, values) => {
        if (err) return;
        this.loginLoading = true;
        try {
          if (this.rememberMe) {
            Cookies.set("gate_username", values.username, { expires: 30 });
          } else {
            Cookies.remove("gate_username");
            Cookies.remove("gate_role");
          }
          const payload = {
            username: values.username,
            password: values.password,
            code: values.code || "",
            uuid: this.captchaUuid,
          };
          if (this.currentRole === "sponsor") {
            await this.$store.dispatch("user/LoginSponsor", payload);
          } else if (this.currentRole === "smo") {
            await this.$store.dispatch("user/LoginSmo", {
              ...payload,
              smoMenuType: values.smoMenuType || "2",
            });
          } else if (this.currentRole === "agent") {
            const params = {
              username: values.username,
              password: values.password,
              captcha_code: values.code || "",
              captcha_key: this.captchaUuid,
            };
            await this.$store.dispatch("user/LoginAgent", params);
          } else {
            // institution: 先获取机构列表，弹窗让用户选择
            this.pendingInstitutionCredentials = {
              username: values.username.trim(),
              password: values.password,
            };
            this.institutionSites = [];
            this.selectedSiteId = null;
            this.institutionSiteLoading = true;
            try {
              const sites = await this.$store.dispatch(
                "user/FetchInstitutionSites",
                values.username.trim()
              );
              this.institutionSites = sites;
              if (sites.length === 1) {
                // 只有一个机构，直接登录无需弹窗
                await this.$store.dispatch("user/LoginInstitution", {
                  ...this.pendingInstitutionCredentials,
                  site_id: sites[0].site_id,
                });
              } else {
                // 多个机构，弹窗让用户选择
                this.institutionModalVisible = true;
              }
            } catch {
              this.institutionModalVisible = false;
            } finally {
              this.institutionSiteLoading = false;
              this.loginLoading = false;
            }
            return;
          }
        } catch (error) {
          if (error && error.code === 901) {
            const [errorCount, maxAttempts] = error.data || [0, 6];
            const remaining = maxAttempts - errorCount;
            this.$message.error(
              remaining > 0
                ? `密码错误，还剩 ${remaining} 次机会`
                : "账号已锁定，请联系管理员"
            );
          }
          if (this.currentRole !== "institution") {
            this.refreshCaptcha();
            this.loginForm.setFieldsValue({ code: "" });
          }
        } finally {
          this.loginLoading = false;
        }
      });
    },
    loadSavedCredentials() {
      const username = Cookies.get("gate_username");
      const role = Cookies.get("gate_role");
      if (username && role) {
        this.rememberMe = true;
        this.currentRole = role;
        this.$nextTick(() => {
          this.loginForm.setFieldsValue({ username });
          if (role !== "institution") this.refreshCaptcha();
        });
      } else {
        // 无记住密码时，仍恢复上次选择的角色
        const lastRole = localStorage.getItem("gate_last_role");
        if (lastRole) {
          this.currentRole = lastRole;
          this.$nextTick(() => {
            if (lastRole !== "institution") this.refreshCaptcha();
          });
        }
      }
    },
    openForgotModal() {
      if (!this.currentRole) {
        this.$message.warning("请先选择角色后再找回密码");
        return;
      }
      this.forgotVisible = true;
    },
    async confirmInstitutionSite() {
      if (!this.selectedSiteId) {
        this.$message.warning("请选择登录机构");
        return;
      }
      this.loginLoading = true;
      try {
        await this.$store.dispatch("user/LoginInstitution", {
          ...this.pendingInstitutionCredentials,
          site_id: this.selectedSiteId,
        });
      } catch {
        // 错误信息已由拦截器展示
      } finally {
        this.loginLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
}
/* ===== 全页背景 ===== */
.gate-page {
  min-height: 100vh;
  background-image: url("~@/assets/images/bg.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  font-family: "PingFang SC", "Microsoft YaHei", -apple-system, sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 40px 0;
  .logo {
    width: 100px;
    height: 100px;
    position: absolute;
    top: 30px;
    left: 30px;
    z-index: 8;
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
}

/* ===== 主卡片容器 ===== */
.page-body {
  width: 100%;
  max-width: 1200px;
  min-height: 480px;
  display: flex;
  justify-content: center;
}

.platform-card {
  display: flex;
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
}

/* ===== 左侧品牌列 ===== */
.brand-col {
  width: 440px;
  flex-shrink: 0;
  background: #e6fdff;
  background-image: url("~@/assets/images/right-panel-bg.png");
  background-size: 100% 100%;
  background-position: 0 100%;
  background-repeat: no-repeat;
  padding: 32px 32px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  height: 629px;
}
.brand-col-no-current {
  height: 513px;
  background-image: url("~@/assets/images/right-panel-bg-short.png");
}
.brand-col-main {
  // height: 600px;
}

/* Q&A 徽章 */
.brand-qa-badge {
  position: absolute;
  top: 20px;
  right: 20px;
  background: linear-gradient(135deg, #3aa8c1 0%, #1677ff 100%);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  letter-spacing: 0.5px;
}

.main-title {
  font-size: 2rem;
  font-weight: 800;
  color: #235bc7;
  line-height: 1.25;
  .dot {
    color: #235bc7;
    font-weight: 400;
  }
}

.subhead {
  font-size: 0.82rem;
  color: #235bc7;
  margin-top: -4px;
  line-height: 1.6;
}

/* 滚动状态 */
.landing-status {
  background: #fff;
  border-radius: 8px;
  padding: 12px 14px;
}

.landing-status-title {
  display: flex;
  align-items: center;
  font-size: 0.96rem;
  font-weight: 700;
  color: #235bc7;
  margin-bottom: 8px;
  .landing-status-icon {
    width: 1.5rem;
    height: 1.5rem;
    object-fit: contain;
    margin-right: 6px;
  }
}

.landing-status-window {
  height: 100px;
  overflow: hidden;
}

.landing-status-track {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  animation: statusScroll 16s linear infinite;
  color: #235bc7;
  &.paused {
    animation-play-state: paused;
  }
}

@keyframes statusScroll {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-50%);
  }
}

.landing-status-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.78rem;
  color: #235bc7;
  line-height: 1.5;
  cursor: default;
  overflow: hidden;
}

.status-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #1fc887;
  flex-shrink: 0;
  margin-top: 6px;
}

.landing-status-text {
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
}

/* ===== 右侧交互列 ===== */
.interact-col {
  flex: 1;
  background: #fff;
  padding: 32px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
}

/* 角色卡片 */
.role-grid {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  width: 720px;
}

.role-card {
  height: 110px;
  flex: 1;
  border: 1.5px solid #235bc7;
  color: #235bc7;
  border-radius: 10px;
  padding: 10px;
  text-align: center;
  cursor: pointer;

  .role-icon {
    img {
      width: 36px;
      height: 36px;
      object-fit: contain;
      margin: 0 auto;
      display: block;
    }
  }

  .role-name {
    font-size: 1.3rem;
    font-weight: 600;
    color: #235bc7;
  }

  .role-desc {
    font-size: 0.65rem;
    color: #456fe6;
    line-height: 1.1;
  }

  &:hover,
  &.active {
    background: #235bc7;
    color: #fff;
    .role-name {
      color: #fff;
    }
    .role-desc {
      color: #fff;
    }
  }
}

/* 登录表单动画 */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}
.slide-down-enter,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.login-form-wrap {
  width: 100%;
  padding-top: 18px;
  margin-bottom: 8px;
}

/* 验证码行 */
.captcha-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.captcha-box {
  width: 120px;
  height: 40px;
  flex-shrink: 0;
  border: 1px solid #c8d8e8;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  background: #fff;
  font-size: 0.78rem;
  color: #8ca0b8;
  img {
    width: 100%;
    height: 100%;
    object-fit: fill;
  }
}

/* 记住密码行 */
.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 2px 0 14px;
}

.forgot-link {
  font-size: 0.78rem;
  color: red;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}

/* 登录按钮 */
.btn-login {
  border-radius: 10px !important;
  font-weight: 700 !important;
  font-size: 0.92rem !important;
  height: 44px !important;
  background: linear-gradient(135deg, #1677ff 0%, #0e5fd8 100%) !important;
  border: none !important;
  box-shadow: none !important;

  &:hover {
    background: linear-gradient(135deg, #0e5fd8 0%, #0a4ab8 100%) !important;
  }
}

/* 底部 */
.card-footer {
  color: #000;
  margin-top: 10px;
  height: 80px;

  .no-login-footer {
    font-size: 1rem;
    text-align: center;
    color: #456fe6;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
  .login-footer {
    height: 40px;
  }
}

/* 响应式 */
@media (max-width: 960px) {
  .gate-page {
    padding: 32px 0;
  }
  .brand-col {
    width: 360px;
  }
}

@media (max-width: 760px) {
  .platform-card {
    flex-direction: column;
  }
  .brand-col {
    width: 100%;
    background-size: cover;
    background-position: center;
    min-height: 280px;
  }
  .interact-col {
    padding: 28px 24px;
  }
}

@media (max-width: 480px) {
  .role-grid {
    flex-direction: column;
  }
  .gate-page {
    padding: 20px 0;
  }
}
</style>
<style>
.cursor-pointer {
  cursor: pointer;
}
</style>
